import { Component, Input } from '@angular/core';
import { Invoice } from 'src/app/shared/models/invoice.interface';

/**
 * House Bill of Lading PDF Format Component
 *
 * This component renders invoices in a house bill of lading format
 * that displays detailed shipping and cargo information including:
 * - Bill of lading number and vessel information
 * - Shipper and consignee details
 * - Port of loading and discharge information
 * - Cargo description and package details
 * - Freight and shipping terms
 * - Company certification and signatures
 *
 * Supports both individual and merged invoice modes.
 */
@Component({
  selector: 'app-house-bill-landing',
  templateUrl: './house-bill-landing.component.html',
  styleUrls: ['./house-bill-landing.component.scss']
})
export class HouseBillLandingComponent {
  @Input() invoices: Invoice[] = [];
  @Input() viewMode: string = 'individual';

  constructor() { }

  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatCurrency(amount: number | null, currency: string | null = 'USD'): string {
    if (!amount || amount === 0) return '';
    const curr = currency || 'USD';
    return `${curr} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Helper method to get vehicle count for display
  getVehicleCount(invoice: any): number {
    if (this.viewMode === 'merged' && invoice.vehicles) {
      return invoice.vehicles.length;
    }
    return 1;
  }

  // Helper method to safely access nested properties
  safeGet(obj: any, path: string, defaultValue: string = ''): string {
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    return value || defaultValue;
  }

  // Generate bill of lading number based on invoice
  getBillOfLadingNumber(invoice: any): string {
    return `BL-${invoice.invoice_number || '000000'}`;
  }

  // Get current date for bill of lading
  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Get vessel name (placeholder - can be customized based on data)
  getVesselName(): string {
    return 'MV IMPERIAL CARRIER';
  }

  // Get voyage number (placeholder - can be customized based on data)
  getVoyageNumber(): string {
    return 'VOY-' + new Date().getFullYear() + '-' + (new Date().getMonth() + 1).toString().padStart(2, '0');
  }

  // Get total packages count
  getTotalPackages(invoice: any): number {
    if (this.viewMode === 'merged' && invoice.vehicles) {
      return invoice.vehicles.length;
    }
    return 1;
  }

  // Get cargo description
  getCargoDescription(invoice: any): string {
    if (this.viewMode === 'merged' && invoice.vehicles) {
      return `${invoice.vehicles.length} USED VEHICLES`;
    }
    return `1 USED VEHICLE - ${invoice.vehicle?.make || ''} ${invoice.vehicle?.model || ''}`;
  }
}
